import { api } from './client.js'
import { endpoints } from './endpoints.js'

// Django SimpleJWT expects `{ username, password }`. We accept either
// an email-looking string or a username from the UI and forward it as
// `username` — Django's default User model authenticates by username.
export async function login(usernameOrEmail, password) {
  const { data } = await api.post(endpoints.login, {
    username: usernameOrEmail,
    password,
  })
  // { access, refresh }  OR  { mfa_required: true, mfa_token, method }
  return data
}

// ---- MFA (email OTP) -------------------------------------------------------

/**
 * Login step 2: exchange the mfa_token + emailed 6-digit code (or a recovery
 * code) for the real JWT pair. Throws on a wrong/expired code (401).
 */
export async function verifyMfa(mfaToken, code) {
  const { data } = await api.post(endpoints.mfaVerify, {
    mfa_token: mfaToken,
    code,
  })
  return data // { access, refresh }
}

/** Turn on email MFA for the signed-in user. Returns recovery codes (once). */
export async function enableMfa() {
  const { data } = await api.post(endpoints.mfaEnable)
  return data // { enabled, method, recovery_codes }
}

export async function disableMfa() {
  const { data } = await api.post(endpoints.mfaDisable)
  return data
}

export async function regenerateRecoveryCodes() {
  const { data } = await api.post(endpoints.mfaRecoveryCodes)
  return data // { recovery_codes }
}

export async function getMfaStatus() {
  const { data } = await api.get(endpoints.mfaStatus)
  return data
}

// ---- TOTP / authenticator app (Phase 2) ------------------------------------

/** Begin authenticator enrolment. Returns { secret, otpauth_uri, qr_data_uri }. */
export async function totpSetup() {
  const { data } = await api.post(endpoints.mfaTotpSetup)
  return data
}

/** Confirm enrolment with a code. Returns { enabled, recovery_codes }. */
export async function totpConfirm(code) {
  const { data } = await api.post(endpoints.mfaTotpConfirm, { code })
  return data
}

/** Remove the authenticator (email MFA, if any, is left intact). */
export async function totpDisable() {
  const { data } = await api.post(endpoints.mfaTotpDisable)
  return data
}

/** Login fallback: a TOTP user requests an email OTP using their mfa_token. */
export async function sendMfaEmailCode(mfaToken) {
  const { data } = await api.post(endpoints.mfaSendEmailCode, { mfa_token: mfaToken })
  return data
}

/**
 * Platform-admin: unlock (or with disable=true fully reset) a user's MFA.
 * Pass either a numeric `userId` or a `username`.
 */
export async function adminResetMfa({ userId, username, disable = false } = {}) {
  const body = { disable }
  if (userId != null && userId !== '') body.user_id = userId
  if (username) body.username = username
  const { data } = await api.post(endpoints.mfaAdminReset, body)
  return data
}

export async function getMe() {
  const { data } = await api.get(endpoints.me)
  return data
}

// No server-side logout endpoint; client just clears its tokens.
export async function logout() {
  return null
}

/**
 * Public patient self-registration. Returns { access, refresh, user } so the
 * caller can store tokens and immediately treat the user as signed-in —
 * no follow-up login round-trip needed.
 *
 * @param {object} data
 * @param {string} data.first_name
 * @param {string} data.last_name
 * @param {string} data.sex                'male' | 'female' | 'other'
 * @param {string} data.password           min 8 chars
 * @param {string=} data.username          optional; derived if omitted
 * @param {string=} data.email
 * @param {string=} data.date_of_birth     ISO date, e.g. '1990-05-14'
 * @param {string=} data.phone_number
 * @param {string=} data.place_of_residence
 */
export async function registerPatient(data) {
  const { data: resp } = await api.post(endpoints.registerPatient, data)
  return resp
}

/**
 * Request a password reset link for the given email. Always resolves
 * with the same generic 200 message regardless of whether the email
 * exists — that's by design (don't leak which addresses are registered).
 */
export async function requestPasswordReset(email) {
  const { data } = await api.post(endpoints.passwordResetRequest, { email })
  return data
}

/**
 * Confirm a password reset using the uid + token from the email link.
 * Returns the success detail message; throws on invalid/expired token.
 */
export async function confirmPasswordReset({ uid, token, new_password }) {
  const { data } = await api.post(endpoints.passwordResetConfirm, {
    uid, token, new_password,
  })
  return data
}
